import React, { useEffect, useState } from "react";
import { Form, Input, Typography, Card, Button, Spin, Checkbox, notification } from "antd";
import { submitPartnershipForm } from "../../api";
import "./PartnershipForm.css";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const industryOptions = [
    "BioTech/Pharma",
    "FinTech",
    "EdTech",
    "HealthTech",
    "SpaceTech",
    "Longevity",
    "Information Technology",
    "FoodTech",
    "Other",
];

const cooperationOptions = [
    "Establishment of Joint Ventures, Projects and Products",
    "White Label Solutions",
    "Make Business Development Relations, Partnerships on Our Behalf via our Partnership Platform",
    "Co-Investment in Deep Knowledge Group Projects",
    "SaaS",
    "Sales Representative",
    "Strategic Alliance",
    "Referral Partnership",
    "Cross-Promotion Partnership",
    "Co-Marketing Partnership",
    "Other",
];

const PartnershipForm = ({ selectedResponse = null }) => {
    const [editable, setEditable] = useState(!selectedResponse);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [time, setTime] = useState(true);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (selectedResponse) {
            form.setFieldsValue(selectedResponse);
            setEditable(false);
        }
        setTimeout(() => {
            setTime(false);
        }, 1500);
    }, [selectedResponse, form]);

    const handleSubmit = () => {
        setLoading(true);
        form
            .validateFields()
            .then((values) => {
                submitPartnershipForm(values)
                    .then(() => {
                        notification.success({ message: "Form submitted successfully!" });
                        setIsFormSubmitted(true);
                        setLoading(false);
                    })
                    .catch((error) => {
                        notification.error({ message: error?.response?.data?.message || "Failed to submit form!" });
                        setLoading(false);
                    });
            })
            .catch(() => {
                notification.error({ message: "Please correct form errors before submitting." });
                setLoading(false);
            });
    };

    return (
        <div style={{ maxWidth: 800, margin: "auto", padding: 20 }}>
            {loading && <div className="fixed-loader">
                <Spin size="large" />
            </div>}
            {isFormSubmitted ? (
                <Card style={headerStyle}>
                    <Title level={2}>Deep Knowledge Group Partnership Interest Form</Title>
                    <Paragraph>Thank you for submitting the form. Please check your email to continue.</Paragraph>
                </Card>
            ) : (
                time ? <Spin size="large" style={{width: '100%'}} />
                    :
                    <>
                        <Card style={headerStyle}>
                            <Title level={2}>Deep Knowledge Group Partnership Interest Form</Title>
                            <Paragraph style={{ fontSize: 16 }}>
                                <strong>Partnership Platform is an advanced business development ecosystem designed to equip external partners with the tools and infrastructure.</strong>
                            </Paragraph>
                            <Paragraph>Becoming a partner with Deep Knowledge Group is a streamlined process designed to foster collaboration and mutual growth.</Paragraph>
                            <Paragraph>Begin the process by completing the partnership form, providing key details about your expertise and interest areas.</Paragraph>
                        </Card>

                        <Form form={form} layout="vertical">
                            {renderSection("Full Name", "fullName", true, {}, editable, form)}
                            {renderSection("Email", "email", true, { type: "email" }, editable, form, [
                                { required: true, message: "Email is required" },
                                { type: "email", message: "Please enter a valid email address" },
                            ])}
                            {renderSection("LinkedIn Profile", "linkedIn", true, {}, editable, form, [
                                { required: true, message: "LinkedIn Profile is required" },
                                { type: "url", message: "Please enter a valid LinkedIn URL (https://...)" },
                            ])}
                            {renderSection("Company Name", "companyName", false, {}, editable, form)}
                            {renderSection("Job Title", "jobTitle", false, {}, editable, form)}

                            {/* Industry Section with single-column checkboxes */}
                            <Card style={sectionStyle}>
                                <Form.Item label={<LabelWithAsterisk label="Industry" required />} name="industry" rules={[{ required: true, message: "Please select at least one industry" }]}>
                                    {editable ? (
                                        <Checkbox.Group style={checkboxColumnStyle} options={industryOptions} />
                                    ) : (
                                        <Paragraph>{form.getFieldValue("industry")?.join(", ") || "N/A"}</Paragraph>
                                    )}
                                </Form.Item>
                            </Card>

                            {/* Country Section */}
                            {renderSection("Country", "country", true, {}, editable, form)}

                            {/* Scenario for Cooperation Section with single-column checkboxes */}
                            <Card style={sectionStyle}>
                                <Form.Item
                                    label={<LabelWithAsterisk label="Scenario for Cooperation" required />}
                                    name="cooperationScenario"
                                    rules={[{ required: true, message: "Please select at least one scenario" }]}
                                >
                                    {editable ? (
                                        <Checkbox.Group style={checkboxColumnStyle} options={cooperationOptions} />
                                    ) : (
                                        <Paragraph>{form.getFieldValue("cooperationScenario")?.join(", ") || "N/A"}</Paragraph>
                                    )}
                                </Form.Item>
                            </Card>

                            {/* Additional Comments Section */}
                            <Card style={sectionStyle}>
                                <Form.Item label="Additional Comments or Questions" name="comments">
                                    {editable ? <TextArea rows={4} placeholder="Enter any additional comments or questions" /> : <Paragraph>{form.getFieldValue("comments") || "N/A"}</Paragraph>}
                                </Form.Item>
                            </Card>

                            {editable && (
                                <Button type="primary" onClick={handleSubmit}>
                                    Submit
                                </Button>
                            )}
                        </Form>
                    </>
            )}
        </div>
    );
};

// Reusable function to render a form section with label formatting
const renderSection = (label, name, required = false, inputProps = {}, editable = true, form, rules = []) => (
    <Card style={sectionStyle} key={name}>
        <Form.Item label={<LabelWithAsterisk label={label} required={required} />} name={name} rules={rules}>
            {editable ? <Input placeholder={`Enter your ${label.toLowerCase()}`} {...inputProps} /> : <Paragraph>{form.getFieldValue(name) || "N/A"}</Paragraph>}
        </Form.Item>
    </Card>
);

// Component for rendering label with an optional red asterisk
const LabelWithAsterisk = ({ label, required }) => (
    <span style={labelStyle}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
    </span>
);

// Styles
const headerStyle = { borderRadius: 8, border: "1px solid #d9d9d9", boxShadow: "0px 2px 5px rgba(0,0,0,0.1)", padding: 20, marginBottom: 20 };
const sectionStyle = { borderRadius: 8, border: "1px solid #d9d9d9", boxShadow: "0px 2px 5px rgba(0,0,0,0.1)", padding: 20, marginBottom: 20 };
const labelStyle = { fontSize: "16px", fontWeight: "500", marginBottom: "8px", display: "inline-block" };
const checkboxColumnStyle = { display: "flex", flexDirection: "column", gap: "10px" }; // Ensures checkboxes are in one column with spacing

export default PartnershipForm;
